import { useMemo } from 'react';
import { Icon, Spinner } from '@iq/react-components';

import DegradedQualityIndicator from '../shared/DegradedQualityIndicator';
import { resolveThresholdItems } from '../../schemas/thresholdSchema';
import LabelWithTooltip from '../../../../LabelWithTooltip';
import { resolveColor } from '../../../../../colors';

const Status = ({ value, thresholds, variables, loading, isDataInvalid }) => {
  if (loading) return <Spinner />;

  const sortedThresholds = resolveThresholdItems(thresholds || {}, variables);

  // handle legacy thresholds (plain array without thresholdType)
  let resolvedThresholds = sortedThresholds;
  if (resolvedThresholds.length === 0 && Array.isArray(thresholds)) {
    resolvedThresholds = thresholds.toSorted(({ value: v1, max: m1 }, { value: v2, max: m2 }) => {
      if ((v1 || v1 === 0) && (v2 || v2 === 0)) return v1 - v2;
      return m1 - m2;
    });
  }

  if (isDataInvalid) {
    const icon = {
      name: 'he-statuswarning',
      className: 'status-visualization-component-icon',
    };
    return (
      <DegradedQualityIndicator
        message="Component status is invalid"
        icon={icon}
      />
    );
  }

  if (!Array.isArray(resolvedThresholds) || resolvedThresholds.length === 0) {
    return (
      <Icon
        icon="he-help"
        className="status-visualization-component-icon"
      />
    );
  }

  const status = resolvedThresholds.find(
    ({ value: thresholdValue, max }) => (thresholdValue || max) >= value
  );

  if (status !== undefined) {
    return (
      <>
        <Icon
          icon="he-statushealthy"
          className="status-visualization-component-icon"
          style={{
            color: resolveColor(status.fill),
          }}
        />
        <p
          style={{
            color: resolveColor(status.fill),
          }}
        >
          {status.description}
        </p>
      </>
    );
  }
  return (
    <Icon
      icon="he-statusrisk"
      className="status-visualization-component-icon"
      style={{ color: 'red' }}
    />
  );
};

const StatusVisualization = ({
  visualization: { name, variables = [], configuration = {} },
  isPreview = false,
  disconnected,
  latestValues: values,
  initialLoaded: initialLoadedOverride,
  loading: loadingOverride,
}) => {
  const initialLoaded = initialLoadedOverride ?? Boolean(values);
  const loading = loadingOverride ?? !values;
  const { label = '', thresholds } = configuration;

  const [value, quality] = useMemo(() => {
    if (initialLoaded && values) {
      const [firstVariable] = values;
      if (!firstVariable?.value || firstVariable.value.length === 0) {
        return [null, 2];
      }
      const [, lastValue, lastValueQuality = 1] = firstVariable.value;
      if (lastValue || lastValue === 0) {
        const currentValue = Number.isNaN(lastValue) ? null : lastValue;
        return [currentValue, currentValue === null ? 2 : lastValueQuality];
      }
      return [null, 2];
    }
    return [null, 0];
  }, [initialLoaded, values]);

  // invalidate if quality !== 1 (valid) or no data connectivity (disconnected)
  const isDataInvalid = quality > 1 || disconnected;

  return (
    <div className={`status-visualization-component ${isPreview ? 'preview' : 'normal'}`}>
      <span className="status-visualization-label truncateText">
        <LabelWithTooltip name={label || name || '\u00A0'} />
      </span>
      <Status
        value={value}
        thresholds={thresholds}
        variables={variables}
        loading={loading}
        isDataInvalid={isDataInvalid}
      />
    </div>
  );
};

export default StatusVisualization;
